/**
 * Surface content — 3.8 Go-live (cutover runbook).
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * date, window and sentence this surface renders is declared here or read
 * from `@/demo/data`.
 *
 * CANONICAL TIME/STATE RESOLUTION (build brief §0): NOW is Thu 27 Aug 2026 and
 * Van Dijk's phase is BUILD — go-live is 11 weeks out. So this surface is the
 * runbook DRAFT being maintained ahead of the cutover, not a live cutover
 * console: the ribbon reads `build` while the context pill reads GO-LIVE ·
 * 12 NOV.
 *
 * The deck's "71 of 84 key users trained" is resolved the same way. Training
 * has not happened during Build, so the figure is rendered honestly as the
 * TRAINING PLAN — 84 key users in scope, 71 with a scheduled seat — rather
 * than as a completion claim the timeline cannot support. The go/no-go
 * criterion still states the completion threshold (≥ 95%), which is what the
 * plan is measured against on 6 Nov.
 */

import { NOW, VAN_DIJK, agentById, heroProject, personById } from '@/demo/data'
import type { PhaseKey } from '@/demo/data'

const PROJECT = heroProject()
const PLANNER = agentById('cutover-planner')
const OWNER = personById('anouk-de-vries')

/** Whole days from NOW to a demo date. Never Date.now() — NOW is the clock. */
const daysFromNow = (iso: string): number =>
  Math.round((new Date(`${iso}T09:00:00+02:00`).getTime() - NOW.getTime()) / 86_400_000)

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const GO_LIVE_PAGE = {
  moduleLabel: 'PX · Project',
  contextPill: `GO-LIVE · ${VAN_DIJK.goLive.label.toUpperCase()}`,
  /** BUILD, not go-live — the overview and this page describe one world. */
  phase: 'build' as PhaseKey,
  title: 'Go-live — cutover runbook',
  standfirst: `Cutover is ${VAN_DIJK.goLive.weeksAway} weeks out. The cutover planner keeps the runbook current from this project's own history — every rehearsal, every dependency, every person already staffed. The final version, and the call to roll back, belong to a person.`,
  phaseNote:
    'The runbook is drafted during Build and rehearsed through Migrate and Test — it is signed at the go/no-go, not written on the night.',
  overviewLinkLabel: 'Project overview',
  testLinkLabel: 'Test & UAT',
} as const

/** Routes this surface links to — all of them exist in the router. */
export const LINKS = {
  overview: `/project/${PROJECT.id}`,
  test: `/project/${PROJECT.id}/test`,
  migration: `/project/${PROJECT.id}/migration`,
  requirements: `/project/${PROJECT.id}/requirements`,
  training: '/resources/training',
  portal: '/portal',
  planner: `/library/agents/${PLANNER.id}`,
  person: (personId: string): string => `/resources/people/${personId}`,
} as const

/* ── The cutover window and its headline figures ────────────────────────── */

export const CUTOVER = {
  hours: 38,
  from: 'Fri 18:00',
  to: 'Sun 08:00',
  stepsTotal: 64,
  stepsWithRollback: 11,
} as const

export const TRAINING_PLAN = {
  keyUsers: 84,
  scheduled: 71,
  thresholdPct: 95,
} as const

export const GO_NO_GO = {
  date: '2026-11-06',
  dateLabel: '6 Nov',
} as const

export interface GoLiveKpi {
  key: string
  label: string
  figure: string
  qualifier: string
  /** The open criteria carry review weight — the only tinted card in the row. */
  weight?: 'review'
}

export const goLiveKpis = (openCriteria: number): readonly GoLiveKpi[] => [
  {
    key: 'window',
    label: 'Cutover window',
    figure: `${CUTOVER.hours} h`,
    qualifier: `${CUTOVER.from} → ${CUTOVER.to}`,
  },
  {
    key: 'steps',
    label: 'Steps',
    figure: `${CUTOVER.stepsTotal}`,
    qualifier: `${CUTOVER.stepsWithRollback} with a rollback point`,
  },
  {
    key: 'training',
    label: 'Training plan',
    figure: `${TRAINING_PLAN.scheduled} of ${TRAINING_PLAN.keyUsers}`,
    qualifier: 'key users scheduled',
  },
  {
    key: 'go-no-go',
    label: 'Go / no-go',
    figure: GO_NO_GO.dateLabel,
    qualifier: `${openCriteria} criteria open · ${daysFromNow(GO_NO_GO.date)} days from today`,
    weight: 'review',
  },
]

/* ── Runbook table ──────────────────────────────────────────────────────── */

export type OwnerRef =
  | { kind: 'person'; personId: string }
  /** A client-side owner: named, but not a PX resource — no avatar, no profile. */
  | { kind: 'client'; label: string; note: string }
  /** The planner prepares the step; a named engineer runs and signs it. */
  | { kind: 'agent-and-person'; agentId: string; agentLabel: string; personId: string; personLabel: string }

export type RunbookStatusKind = 'ready' | 'rehearsed' | 'depends' | 'staffed'

export type RollbackLabel = 'Restore point' | 'n/a'

export interface RunbookStep {
  key: string
  /** The step's number in the 64-step runbook — rendered mono, not re-indexed. */
  number: number
  step: string
  owner: OwnerRef
  window: string
  rollback: RollbackLabel
  statusKind: RunbookStatusKind
  statusLabel: string
  /** Set on 'depends' rows — the step number this one waits on. */
  dependsOnNumber?: number
}

/** In-page anchor for a step row, so a dependency lands on the step it names. */
export const stepAnchorId = (stepNumber: number): string => `runbook-step-${stepNumber}`

export const RUNBOOK = {
  label: 'Runbook · draft v3',
  heading: 'Cutover steps',
  caption:
    'Eight of the sixty-four steps, in cutover order — the rehearsed ones, the dependencies, and the two the client owns.',
  columns: {
    number: '#',
    step: 'Step',
    owner: 'Owner',
    window: 'Window',
    rollback: 'Rollback',
    status: 'Status',
  },
  dependsLinkSuffix: 'jump to that step',
  clientOwnerNote: 'client-side owner',
} as const

export const RUNBOOK_STEPS: readonly RunbookStep[] = [
  {
    key: 'freeze-legacy',
    number: 1,
    step: 'Freeze the legacy ERP — last order entry, tills closed for the weekend',
    owner: { kind: 'client', label: 'Client IT', note: 'Van Dijk in-house team' },
    window: 'Fri 18:00 – 18:30',
    rollback: 'n/a',
    statusKind: 'ready',
    statusLabel: 'Ready',
  },
  {
    key: 'delta-export',
    number: 3,
    step: 'Final delta export — orders, stock, open invoices, loyalty balances',
    owner: { kind: 'client', label: 'Client IT', note: 'Van Dijk in-house team' },
    window: 'Fri 18:30 – 20:15',
    rollback: 'n/a',
    statusKind: 'ready',
    statusLabel: 'Ready',
  },
  {
    key: 'snapshot',
    number: 7,
    step: 'Snapshot the production database and object storage before any load',
    owner: { kind: 'person', personId: 'yll-gashi' },
    window: 'Fri 20:15 – 21:00',
    rollback: 'Restore point',
    statusKind: 'rehearsed',
    statusLabel: 'Rehearsed 3×',
  },
  {
    key: 'load-master-data',
    number: 12,
    step: 'Load master data and open balances into Odoo 19 production',
    owner: {
      kind: 'agent-and-person',
      agentId: PLANNER.id,
      agentLabel: 'Agent',
      personId: 'yll-gashi',
      personLabel: 'Yll',
    },
    window: 'Fri 21:00 – Sat 02:30',
    rollback: 'Restore point',
    statusKind: 'rehearsed',
    statusLabel: 'Rehearsed 3×',
  },
  {
    key: 'reconcile-stock',
    number: 24,
    step: 'Reconcile store stock against the Friday close, store by store',
    owner: { kind: 'person', personId: 'anouk-de-vries' },
    window: 'Sat 02:30 – 06:00',
    rollback: 'n/a',
    statusKind: 'staffed',
    statusLabel: 'Staffed · KS + NL',
  },
  {
    key: 'switch-integrations',
    number: 31,
    step: 'Switch the payment and webshop integrations to the Odoo endpoints',
    owner: { kind: 'person', personId: 'yll-gashi' },
    window: 'Sat 09:00 – 11:30',
    rollback: 'Restore point',
    statusKind: 'rehearsed',
    statusLabel: 'Rehearsed 3×',
  },
  {
    key: 'till-smoke-test',
    number: 38,
    step: 'Smoke-test the tills in four pilot stores, then release the rest',
    owner: { kind: 'person', personId: 'anouk-de-vries' },
    window: 'Sat 12:00 – 15:00',
    rollback: 'n/a',
    statusKind: 'depends',
    statusLabel: 'Depends on 31',
    dependsOnNumber: 31,
  },
  {
    key: 'rollback-call',
    number: 57,
    step: 'The call: open on Odoo on Monday, or restore the legacy snapshot',
    owner: { kind: 'person', personId: 'anouk-de-vries' },
    window: 'Sun 06:00 – 06:30',
    rollback: 'Restore point',
    statusKind: 'staffed',
    statusLabel: 'Staffed · KS + NL',
  },
]

/* ── Go / no-go criteria ────────────────────────────────────────────────── */

export interface GoNoGoCriterion {
  key: string
  criterion: string
  /** What is true today — the reason it is still open. */
  standing: string
  statusLabel: string
  href: string
  linkLabel: string
}

export const GO_NO_GO_PANEL = {
  label: 'Go / no-go criteria',
  heading: `Four criteria, open until ${GO_NO_GO.dateLabel}`,
  caption:
    'Nothing here is asserted on this page — each criterion links to the surface that proves it, and the go/no-go reads that surface, not this list.',
} as const

export const GO_NO_GO_CRITERIA: readonly GoNoGoCriterion[] = [
  {
    key: 'uat-signoff',
    criterion: 'UAT sign-off received from the client',
    standing: `${VAN_DIJK.requirements.approved} of ${VAN_DIJK.requirements.total} requirements approved · ${VAN_DIJK.requirements.open} still open`,
    statusLabel: 'Open',
    href: LINKS.test,
    linkLabel: 'Test & UAT',
  },
  {
    key: 'trial-4',
    criterion: `Migration trial ${VAN_DIJK.migration.trialLoads.total} passed with no blocking errors`,
    standing: `${VAN_DIJK.migration.trialLoads.done} of ${VAN_DIJK.migration.trialLoads.total} trial loads done · next rehearsal ${VAN_DIJK.migration.trialLoads.nextRehearsal} · ${VAN_DIJK.migration.validationErrors.now} validation errors left`,
    statusLabel: 'Open',
    href: LINKS.migration,
    linkLabel: 'Migration',
  },
  {
    key: 'training-completion',
    criterion: `Training completion at or above ${TRAINING_PLAN.thresholdPct}% of key users`,
    standing: `${TRAINING_PLAN.scheduled} of ${TRAINING_PLAN.keyUsers} key users have a scheduled seat · sessions start after the Test gate`,
    statusLabel: 'Open',
    href: LINKS.training,
    linkLabel: 'Training center',
  },
  {
    key: 'hypercare-roster',
    criterion: 'Client hypercare roster confirmed by the sponsor',
    standing: 'The client names its own weekend and first-week contacts in the portal',
    statusLabel: 'Open',
    href: LINKS.portal,
    linkLabel: 'Client portal',
  },
]

/* ── Hypercare (deck-verbatim shape) ────────────────────────────────────── */

export const HYPERCARE = {
  label: 'Hypercare',
  heading: 'Two weeks, 07:00 – 19:00 CET',
  lines: [
    'Two weeks of hypercare after cutover, covered 07:00 – 19:00 CET.',
    'Kosovo takes the early shift; the Netherlands takes the late one, so the handset is never unattended during store hours.',
    'Every hypercare ticket is tagged to the requirement it belongs to — so the first two weeks read back as evidence against the register, not as a pile of incidents.',
  ],
  requirementsLinkLabel: 'Requirements register',
  requirementsHref: LINKS.requirements,
} as const

/* ── Adoption (mint — the human side the planner does not touch) ─────────── */

export const ADOPTION = {
  label: 'Adoption stays with people',
  lines: [
    'Training, floor-walking and the first month of store support are led by named consultants — the same faces the client met in Discover.',
    'Agents prepare the material: quick-reference cards from the blueprint, per-role scripts, the hypercare FAQ. They do not face the client.',
  ],
  consultantsLabel: 'Leading adoption',
  consultants: [
    { personId: 'endrit-krasniqi', note: 'floor-walking · pilot stores' },
    { personId: 'femke-de-boer', note: 'key-user training · first month of support' },
  ],
  agentChip: `Prepared by ${PLANNER.name}`,
} as const

/* ── Ownership (a DRAFT — no approved-by chip; nothing is signed yet) ────── */

/** The step at which the rollback call is actually made — never re-typed. */
const ROLLBACK_CALL_STEP = RUNBOOK_STEPS.find((s) => s.key === 'rollback-call')?.number ?? 0

export const OWNERSHIP = {
  label: 'Ownership',
  heading: 'Runbook v3 — drafted, not signed',
  draftedLine: `${PLANNER.name} drafts the runbook from this project's own history: the build board, the migration rehearsals and the test runs. Its gate is "${PLANNER.reviewGate}" — the draft never becomes the version.`,
  ownerLine: `${OWNER.name} (${OWNER.role}) owns the final version and makes the rollback call at step ${ROLLBACK_CALL_STEP} on Sunday morning.`,
  goNoGoLine: `The version is signed at the go/no-go on ${GO_NO_GO.dateLabel}, ${daysFromNow(GO_NO_GO.date)} days from today — while all four criteria are open, nothing is approved and this page says so.`,
  draftStatusLabel: 'Draft · not yet signed',
  agentChipLabel: `${PLANNER.name} · ${PLANNER.defaultAutonomy} · accepted ${PLANNER.acceptancePct}%`,
  ownerLinkLabel: OWNER.name,
  ownerPersonId: OWNER.id,
  plannerLinkLabel: PLANNER.name,
  reason:
    'A cutover is a judgement call under time pressure — the person who will make the rollback call owns the final version.',
} as const

/* ── Density note ───────────────────────────────────────────────────────── */

export const densityNote = (shown: number): string =>
  `${shown} of ${CUTOVER.stepsTotal} runbook steps shown, in cutover order — the full runbook is the cutover-night document, printed and held by the person on the call. Rollback detail is withheld to step level here (${CUTOVER.stepsWithRollback} steps carry a restore point; the restore procedure itself lives in the document). Each go/no-go criterion links to the surface that proves it rather than restating its numbers.`
